<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { revokeDiscountFromUser } from '@/api/functions/settings/discounts-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  user: { type: Object },
  discountCode: { type: String, required: true },
})

const emit = defineEmits(['confirmUser'])

const toast = useToast()
const router = useRouter()

const showRevokeModal = ref(false)
const userId = computed(() => props.user?.uuid)
const userUuid = computed(() => props.user?.uuid)
const discountUserId = computed(() => props.user?.discount_user?.id)

const goToUserDetails = () => {
  router.push({
    path: `/users/${userId.value}`,
  })
}

const revokeMutation = useMutationApi({
  mutationFn: () => revokeDiscountFromUser(userUuid.value, props.discountCode, discountUserId.value),
  onSuccess: () => {
    toast.success('تخفیف از کاربر با موفقیت حذف شد')
    emit('confirmUser')
    showRevokeModal.value = false
  },
  onError: (err: unknown) => {
    toast.error(getErrorMessage(err), 'خطا')
  },
})

const handleRevokeDiscount = () => {
  if (!userUuid.value || !discountUserId.value) return
  revokeMutation.mutate()
}
</script>

<template>
  <div
    class="bg-white rounded-xl p-5 lg:p-8 lg:pb-6 relative flex flex-col-reverse lg:flex-col gap-6 lg:gap-0"
  >
    <ConfirmModal
      v-model="showRevokeModal"
      title="حذف تخفیف از کاربر"
      :message="'آیا از حذف این تخفیف از کاربر اطمینان دارید؟'"
      confirm-text="بله حذف شود"
      cancel-text="بازگشت"
      @confirm="handleRevokeDiscount"
    />

    <div class="relative lg:absolute bottom-0 lg:top-8 lg:end-8">
      <div class="w-full lg:w-fit flex items-center">
        <Button
          @click="goToUserDetails"
          :isAdmin="true"
          class="w-1/2 lg:w-auto rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal me-2"
        >
          مشاهده
        </Button>
        <Button
          @click="showRevokeModal = true"
          class="bg-transparent w-1/2 lg:w-auto border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-4 h-10! py-0 text-[13px] font-normal me-2"
        >
          <span class="flex items-center justify-center gap-2 text-[#2d5396]">
            حذف تخفیف از کاربر
          </span>
        </Button>
      </div>
    </div>

    <div class="flex flex-col gap-6 text-black text-sm font-medium">
      <!-- Header -->
      <div class="flex flex-col lg:flex-row items-center justify-center gap-2 lg:justify-start">
        <div
          class="w-full lg:w-fit flex items-center justify-center text-[13px] bg-[#d5ddea] text-[#2d5396] font-medium h-10 rounded-[10px] px-6"
        >
          کد کاربری
          <span class="font-fa ms-1">{{ user?.code }}</span>
          <!-- <span v-if="!isButtonHidden" class="mx-1">|</span> -->
          <span>
            {{
              user?.status === 'VERIFIED'
                ? 'تایید شده'
                : user?.status === 'EMAIL_VERIFICATION_PENDING'
                  ? 'ثبت نام اولیه'
                  : user?.status === 'MOBILE_REGISTRATION_PENDING'
                    ? ' در انتظار ثبت شماره موبایل'
                    : user?.status === 'IDENTIFICATION_REGISTRATION_PENDING'
                      ? ' در انتظار ارسال مدارک هویتی'
                      : user?.status === 'IDENTIFICATION_VERIFICATION_PENDING'
                        ? ' در انتظار تایید مدارک هویتی'
                        : user?.status === 'MOBILE_VERIFICATION_PENDING'
                          ? ' در انتظار تایید شماره موبایل'
                          : 'منقضی شده'
            }}
          </span>
        </div>
      </div>
      <!-- Body -->
      <div class="flex flex-col gap-2.5 text-sm text-black font-medium">
        <div class="flex items-center gap-1">
          <span>نام و نام خانوادگی:</span>

          <span class="">{{ user?.fullName }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span>شماره موبایل:</span>

          <span v-if="user?.mobile" class="font-fa">{{ user?.mobile }}</span>
          <span v-if="user?.mobile" class="font-fa">{{ user?.countryCode }} +</span>
          <span v-else>–</span>
        </div>
        <div class="flex items-center gap-1">
          <span>ایمیل:</span>
          <span>{{ user?.email }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span>کانال ورودی:</span>
          <span>{{ user?.referralType === 'DIRECT' ? 'مستقیم' : 'ریفرال' }}</span>
        </div>
      </div>
      <!-- Footer -->
      <div
        class="flex flex-col lg:flex-row lg:items-center gap-6 mt-2 lg:mt-4 lg:gap-0 pe-1 text-sm text-[#1b325a] font-normal"
        :class="user?.country ? 'justify-between' : 'justify-end'"
      >
        <!-- Country  -->
        <div
          v-if="user?.country"
          class="flex items-center gap-1 text-sm font-medium text-[#000000]"
        >
          <img :src="user?.country?.flagUrl" width="24" height="16" alt="flag" />
          {{ user?.country?.title?.fa }}
        </div>

        <div
          class="flex flex-wrap items-center justify-between lg:justify-end text-sm font-medium gap-4 mb-2"
        >
          <div class="flex items-center">
            ثبت:<span dir="ltr" class="ms-1 font-fa">{{
              stringToFaDateTime(user?.createdAt)
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
